import { AxiosError } from 'axios';
import { IDetailToInvoice } from '../types';

export const TIMESHEET_INVOICES_URL = 'invoicing/timesheet-invoices';

/** Body di `POST /invoicing/timesheet-invoices` (solo provider Previxio). */
export interface ITimesheetInvoiceRequest {
  jobId: number;
  resourceId: number;
  startDate: string;
  endDate: string;
}

export interface ITimesheetInvoiceResponse {
  invoicingRequestId: string;
  provider: string;
  externalInvoiceId: string;
  externalInvoiceNumber: string | null;
  status: string | null;
  activitiesCount: number;
}

interface IProviderMessage {
  field?: string;
  message?: string;
}

/** Body di errore del backend di fatturazione (`code` stabile). */
interface ITimesheetInvoiceErrorBody {
  message?: string;
  code?: string;
  details?: {
    errors?: IProviderMessage[];
    providerMessage?: string;
    providerDetails?: unknown;
    externalInvoiceNumber?: string;
    activities?: { invoiceNumber?: string | null }[];
  };
  invoicingRequestId?: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Codici per cui serve il riferimento della richiesta per la verifica manuale. */
const CODES_WITH_REQUEST_REFERENCE = [
  'INVOICING_OUTCOME_UNKNOWN',
  'INVOICING_IN_PROGRESS',
  'PERSISTENCE_FAILED',
];

/** Vale per entrambi i provider: un ordine del periodo ha già un'attività Fatturata. */
export const hasInvoicedOrders = (detail?: IDetailToInvoice): boolean =>
  !!detail &&
  detail.ordersWithHours.some((orderWithHours) =>
    detail.activities.some(
      (activity) => activity?.orderId === orderWithHours?.order?.id,
    ),
  );

const toMessage = (item: unknown): string | undefined => {
  if (typeof item === 'string') return item;
  const { field, message } = (item ?? {}) as IProviderMessage;
  if (!message) return undefined;
  return field ? `${field}: ${message}` : message;
};

const joinMessages = (items: unknown): string =>
  (Array.isArray(items) ? items : [items])
    .map(toMessage)
    .filter(Boolean)
    .join('; ');

/** Dettagli inoltrati dal backend (già sanificati) per i 400 di Previxio. */
const providerMessages = (body: ITimesheetInvoiceErrorBody, t: Translate) => {
  const details = joinMessages(body.details?.providerDetails ?? []);
  return details ? t('invoice.previxio.providerDetails', { details }) : '';
};

/** Numeri delle fatture che già coprono il periodo (dal 409 ALREADY_INVOICED). */
const invoicedNumbers = (body: ITimesheetInvoiceErrorBody, t: Translate) => {
  const numbers = [
    body.details?.externalInvoiceNumber,
    ...(body.details?.activities ?? []).map(
      ({ invoiceNumber }) => invoiceNumber,
    ),
  ].filter(
    (value, index, all): value is string =>
      Boolean(value) && all.indexOf(value) === index,
  );
  return numbers.length > 0
    ? t('invoice.previxio.alreadyInvoicedNumbers', {
        numbers: numbers.join(', '),
      })
    : '';
};

/** Messaggio leggibile per gli errori 409/422/UNKNOWN restituiti dal backend. */
export const describeTimesheetInvoiceError = (
  error: unknown,
  t: Translate,
): string => {
  const body = ((error as AxiosError)?.response?.data ??
    {}) as ITimesheetInvoiceErrorBody;
  const code = body.code;
  const base = code
    ? t(`invoice.previxio.errors.${code}`, {
        defaultValue: body.message ?? t('invoice.previxio.errors.generic'),
      })
    : body.message ?? t('invoice.previxio.errors.generic');
  const parts = [base];
  if (code === 'VALIDATION_FAILED')
    parts.push(joinMessages(body.details?.errors ?? []));
  if (code === 'ALREADY_INVOICED') parts.push(invoicedNumbers(body, t));
  if (code === 'PROVIDER_ERROR') {
    parts.push(body.details?.providerMessage ?? '', providerMessages(body, t));
  }
  if (
    code &&
    CODES_WITH_REQUEST_REFERENCE.includes(code) &&
    body.invoicingRequestId
  ) {
    parts.push(
      t('invoice.previxio.requestReference', { id: body.invoicingRequestId }),
    );
  }
  return parts.filter(Boolean).join(' — ');
};
